import JWPlayer from '@jwplayer/jwplayer-react';
import { MutableRefObject, useEffect, useState } from 'react';

import { MenuOptions } from '../enums/MenuOptions';
import { setLargePanelOpenCommand } from '../stories/Store/menuSlice';
import { normalizeCalendarDate } from '../utility/calendarDate';
import { useAppDispatch, useAppSelector } from '.';
import { useProgramDetails } from './useProgramDetails';

interface IUseLiveEndOfProgramTimerProps {
    playerRef: MutableRefObject<typeof JWPlayer>
}

export const useLiveEndOfProgramTimer = (props: IUseLiveEndOfProgramTimerProps) => {
    const { playerRef } = props;
    const launchDetails = useAppSelector((state) => state.player.launchDetails);
    const { programDetails } = useProgramDetails(launchDetails?.itemPk ?? '0');
    const [now, setNow] = useState(new Date());
    const [lastLiveEndOfVideoPanelKey, setLastLiveEndOfVideoPanelKey] = useState<string>();
    const clientConfig = useAppSelector((state) => state.clientConfig);

    const dispatch = useAppDispatch();
     
    useEffect(() => {
        const intervalTime = 30 * 1000;

        if (launchDetails?.isWebcast) {
            const interval = setInterval(() => {
                setNow(new Date());
            }, intervalTime);

            const dailySegment = programDetails?.dailySegments
                ?.find((segment, index, dailySegments) => {
                    const startTimeUtc = segment.segments?.[0]?.startTimeUtc;
                    if (!startTimeUtc)
                        return false;

                    const nextStartTimeUtc = dailySegments[index + 1]?.segments?.[0]?.startTimeUtc;
                    const startTime = new Date(startTimeUtc);
                    const nextStartTime = nextStartTimeUtc ? new Date(nextStartTimeUtc) : undefined;
                    return now >= startTime && (!nextStartTime || now < nextStartTime);
                });
            const lastSegment = dailySegment?.segments?.[dailySegment.segments.length - 1];
            const programEndTime = lastSegment?.endTimeUtc
                ? new Date(lastSegment.endTimeUtc)
                : ((!programDetails?.dailySegments?.length || dailySegment) && programDetails?.eventEnd
                    ? new Date(programDetails.eventEnd)
                    : null);

            if (programEndTime) {
                const gracePeriodMinutes = clientConfig?.gracePeriodMinutes ?? 0;
                const extendedEndTime = new Date(programEndTime.getTime() + gracePeriodMinutes * 60000);  
                const isStreamComplete = playerRef.current ? playerRef.current.getState() === 'complete' : false;
                const programDate = dailySegment?.date ? normalizeCalendarDate(dailySegment.date) : undefined;
                const currentProgramDateKey = `${launchDetails.itemPk}:${programDate ?? programEndTime.getTime()}`;
                
                if (now >= extendedEndTime || (isStreamComplete && now >= programEndTime)) {
                    if(lastLiveEndOfVideoPanelKey !== currentProgramDateKey && !launchDetails.isOffAir) {
                        setLastLiveEndOfVideoPanelKey(currentProgramDateKey);
                        dispatch(setLargePanelOpenCommand(MenuOptions.EndOfVideo));
                    }
                }
            }

            return () => clearInterval(interval);
        }
    }, [clientConfig?.gracePeriodMinutes, dispatch, lastLiveEndOfVideoPanelKey, launchDetails.isOffAir, launchDetails?.isWebcast, launchDetails.itemPk, now, playerRef, programDetails]);
};
